import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { hashPassword as hashPasswordUtil } from '../utils/hashPassword';
import { API_BASE_URL, API_ROOT, v2Api } from '../services/apiService';
import { officialTournamentService } from '../services/api/officialTournament.service';
import { tournamentStaffService } from '../services/api/tournamentStaff.service';
import { normalizeManagedTournaments, tournamentStaffHomePath } from './tournamentManagers';
import {
  SESSION_EXPIRED_EVENT,
  clearStoredAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  logoutRemote,
  persistTokenPair,
} from '../services/api/authTokens';
import {
  clearSessionClub,
  clubAppUrl,
  clubRequestHeaders,
  forgetStoredClubSlug,
  getCurrentClubSlug,
  getSessionClub,
  getStaffClubs,
  isClubHost,
  normalizeClubSlug,
  setSessionClub,
  setStaffClubs,
} from '../services/currentClub';

const AuthContext = createContext();

/**
 * Rôle d'interface : rôle 1 → admin ; sinon, avec au moins un droit sur un tournoi officiel de la Ligue
 * (`tournament_access`, docs/OFFICIAL_TOURNAMENTS.md § 4) ou sur un tournoi du club (`managed_tournaments`,
 * docs/TOURNAMENT_MANAGERS.md) → staff de tournoi (vue limitée). L'ancien rôle 2 « Officiel » ne donne plus rien.
 */
function mapStaffRole(roleNum, tournamentAccess = [], managedTournaments = []) {
  const n = Number(roleNum);
  if (n === 1) return 'admin';
  if (tournamentAccess.length > 0 || managedTournaments.length > 0) return 'tournament_staff';
  return 'player';
}

export function isStaffRole(role) {
  return role === 'admin' || role === 'tournament_staff';
}

export {
  OFFICIAL_TOURNAMENTS_PATH,
  MANAGER_HOME_PATH,
  canOnTournament,
  isTournamentManager,
  managedTournament,
  managedTournamentsOf,
  normalizeManagedTournaments,
  tournamentPermissionsFor,
} from './tournamentManagers';

/** Message de déconnexion d'un staff de tournoi dont plus aucun accès n'est ouvert (clôture, retrait). */
export const TOURNAMENT_ACCESS_ENDED_MESSAGE = 'Tes accès au tournoi ont pris fin';

/**
 * Droits du compte sur des tournois officiels, au format mémorisé : `tournament_access` de manager-login
 * (`{ tournament_id, name, organizer_club, role, permissions }`) ou `tournaments` de GET /OfficialTournament/my
 * (`{ id, name, status, start_date, end_date, organizer_club, role, permissions, closed_at }`).
 */
export function normalizeTournamentAccess(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((a) => a && (a.tournament_id || a.id))
    .map((a) => ({
      tournament_id: String(a.tournament_id ?? a.id),
      name: a.name || 'Tournoi officiel',
      organizer_club: a.organizer_club ?? null,
      role: a.role || 'adjoint',
      permissions: Array.isArray(a.permissions) ? a.permissions : [],
      status: a.status ?? null,
      start_date: a.start_date ?? null,
      end_date: a.end_date ?? null,
      closed_at: a.closed_at ?? null,
    }));
}

/** Accès encore ouverts (tournoi non clôturé). */
export function activeTournamentAccess(list) {
  return (list || []).filter((a) => !a.closed_at);
}

/** Page d'accueil du staff : le planning des terrains (Agenda), en mode bar comme en gestion avancée — plus de
 * tableau de bord général (celui de la box league est un onglet de Box). */
export const BAR_HOME_PATH = '/app/terrains';

/** Accueil du staff ; `user` : un staff de tournoi qui gère des tournois du club arrive sur l'écran Matchs. */
export function staffHomePath(role, advanced = true, user = null) {
  if (role === 'tournament_staff') return tournamentStaffHomePath(user);
  return BAR_HOME_PATH;
}

function clearStaffSession() {
  try {
    localStorage.removeItem('user');
  } catch {
    /* ignore */
  }
  clearStoredAuthTokens();
  clearSessionClub();
}

/**
 * Utilisateur staff mémorisé, à partir du `user` de manager-login / switch_club. `response` : la réponse complète, qui
 * porte `tournament_access` et `erp_mode` à côté de `user` (lus aussi dans `user` par tolérance).
 */
function mapStaffUser(playerData, response = {}) {
  const tournamentAccess = normalizeTournamentAccess(playerData.tournament_access ?? response?.tournament_access);
  const managedTournaments = normalizeManagedTournaments(playerData.managed_tournaments ?? response?.managed_tournaments);
  const role = mapStaffRole(playerData.role, activeTournamentAccess(tournamentAccess), managedTournaments);
  return {
    id: playerData.id,
    email: playerData.email,
    firstName: playerData.first_name,
    lastName: playerData.last_name,
    picture: playerData.picture,
    role,
    currentBox: playerData.current_box,
    tournamentAccess,
    managedTournaments,
    erpMode: playerData.erp_mode ?? response?.erp_mode ?? (role === 'tournament_staff' ? 'tournament_staff' : 'club_staff'),
  };
}

/**
 * Relit les droits du compte sur les tournois officiels (GET /OfficialTournament/my) et sur les tournois du club
 * (GET /TournamentStaff/my) et les reporte sur l'utilisateur mémorisé. Retourne l'utilisateur à jour ; `null` pour un
 * staff de tournoi sans plus aucun accès ouvert (à déconnecter). Un appel qui échoue (API pas à jour, réseau) garde la
 * valeur mémorisée : la session reste utilisable, l'API refuse de toute façon ce qui n'est plus permis.
 */
export async function refreshTournamentAccess(user) {
  const [official, managed] = await Promise.all([
    officialTournamentService.my().then(normalizeTournamentAccess, () => null),
    tournamentStaffService.my().then(normalizeManagedTournaments, () => null),
  ]);
  if (official === null && managed === null) return user;
  const tournamentAccess = official ?? normalizeTournamentAccess(user.tournamentAccess);
  const managedTournaments = managed ?? normalizeManagedTournaments(user.managedTournaments);
  if (user.role === 'tournament_staff' && activeTournamentAccess(tournamentAccess).length === 0 && managedTournaments.length === 0) {
    return null;
  }
  const next = { ...user, tournamentAccess, managedTournaments };
  try {
    localStorage.setItem('user', JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

/** Message affiché une fois dans l'ERP après le rechargement (connexion sur un autre club que celui demandé). */
const CLUB_NOTICE_SESSION_KEY = 'courtly.clubNotice';

export function takeClubNotice() {
  try {
    const notice = sessionStorage.getItem(CLUB_NOTICE_SESSION_KEY);
    if (notice) sessionStorage.removeItem(CLUB_NOTICE_SESSION_KEY);
    return notice || '';
  } catch {
    return '';
  }
}

function setClubNotice(notice) {
  try {
    sessionStorage.setItem(CLUB_NOTICE_SESSION_KEY, notice);
  } catch {
    /* ignore */
  }
}

/** Adhésions staff (rôle 1, admin du club) de la réponse de manager-login, au format mémorisé ; undefined si absentes. */
function staffClubsOf(data) {
  if (!Array.isArray(data?.clubs)) return undefined;
  return data.clubs
    .filter((c) => normalizeClubSlug(c?.slug) && Number(c.role) === 1)
    .map((c) => ({ club_id: c.club_id, slug: normalizeClubSlug(c.slug), name: c.name || c.slug, role: Number(c.role) }));
}

/**
 * Clubs accessibles au compte (GET /Player/my_clubs) : son seul club pour un compte de club, tous les clubs actifs
 * pour un super-admin de la Ligue (`player_id` null tant qu'il n'y a pas d'adhésion : switch_club la crée). Liste
 * normalisée, mémorisée (reprise de session sur `?club=`), triée par nom.
 */
export async function fetchMyClubs() {
  const { data } = await v2Api.get('/Player/my_clubs');
  const clubs = (Array.isArray(data) ? data : [])
    .filter((c) => normalizeClubSlug(c?.slug))
    .map((c) => ({
      club_id: c.club_id,
      slug: normalizeClubSlug(c.slug),
      name: c.name || c.slug,
      role: c.role == null ? null : Number(c.role),
      player_id: c.player_id ?? null,
      current: Boolean(c.current),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
  setStaffClubs(clubs);
  return clubs;
}

/** Vrai si l'erreur est le refus de changement de club d'un compte à club unique (403 `single_club`). */
function isSingleClubError(error) {
  return error?.response?.status === 403 && error.response?.data?.error === 'single_club';
}

/**
 * Jetons du staff dans un autre de ses clubs (POST /Player/auth/switch_club) : jetons, utilisateur et club
 * remplacés, ancienne session révoquée. Retourne l'utilisateur, ou null si le compte n'y est pas staff.
 */
async function switchStaffClub(club) {
  const previousRefresh = getStoredRefreshToken();
  // Requête au nom du club du jeton actuel (le club courant peut déjà être le club visé : `?club=`).
  const fromSlug = getSessionClub()?.slug;
  const { data } = await v2Api.post(
    '/Player/auth/switch_club',
    { club_id: club.club_id, device_label: 'erp-web' },
    fromSlug ? { headers: { 'X-Club': fromSlug } } : undefined,
  );
  const userData = adoptSwitchedStaffSession(data, club);
  if (userData && previousRefresh) {
    // L'ancienne session reste valable côté API : révoquée (au mieux).
    void v2Api.post('/Player/auth/logout', { refresh_token: previousRefresh }, { skipAuth: true }).catch(() => {});
  }
  return userData;
}

/**
 * Adopte comme session staff de l'ERP une réponse de `POST /Player/auth/switch_club` (jetons, utilisateur, club) :
 * utilisé par le changement de club de l'ERP et par l'espace Ligue (sélecteur d'espace → ERP d'un club, même
 * origine). Retourne l'utilisateur staff, ou null si le compte n'est pas staff de ce club (rien n'est enregistré).
 */
export function adoptSwitchedStaffSession(data, club) {
  const userData = mapStaffUser(data?.user ?? {}, data);
  if (!isStaffRole(userData.role) || !persistTokenPair(data)) return null;
  setSessionClub(data?.club ?? { id: club?.club_id, slug: club?.slug, name: club?.name });
  localStorage.setItem('user', JSON.stringify(userData));
  return userData;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Message de la page de connexion après une déconnexion forcée (accès au tournoi terminés).
  const [authNotice, setAuthNotice] = useState('');
  const clearAuthNotice = useCallback(() => setAuthNotice(''), []);

  const logout = useCallback(async () => {
    setUser(null);
    await logoutRemote(API_ROOT);
    try {
      localStorage.removeItem('user');
    } catch {
      /* ignore */
    }
    clearSessionClub();
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      clearStaffSession();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      const storedUser = localStorage.getItem('user');
      const hasTokens = Boolean(getStoredAccessToken() || getStoredRefreshToken());
      if (!storedUser || !hasTokens) {
        // Ancien cache user sans JWT → forcer re-login.
        clearStaffSession();
        return null;
      }
      let parsed;
      try {
        parsed = JSON.parse(storedUser);
      } catch {
        clearStaffSession();
        return null;
      }
      if (!isStaffRole(parsed?.role)) {
        clearStaffSession();
        return null;
      }
      // Session d'un autre club que le club demandé (`?club=`, hôte du club) : l'API refuserait le jeton
      // (club_mismatch). Changement de club si le compte en est staff, sinon reconnexion.
      const sessionClub = getSessionClub();
      const slug = getCurrentClubSlug();
      if (sessionClub && slug && sessionClub.slug !== slug) {
        const target = getStaffClubs().find((c) => c.slug === slug);
        const switched = target ? await switchStaffClub(target).catch(() => null) : null;
        if (switched) return switched;
        if (isClubHost()) {
          // L'hôte fixe le club : reconnexion sur ce club.
          clearStaffSession();
          return null;
        }
        // Un compte = un club : retour sur le club de la session (rechargement complet), sans déconnexion.
        setClubNotice(`Ce compte n'a accès qu'au club « ${sessionClub.name} ».`);
        setSessionClub(sessionClub);
        window.location.replace(clubAppUrl(sessionClub.slug, window.location.pathname));
        return new Promise(() => {});
      }
      // Droits sur les tournois officiels relus à chaque démarrage (clôture, retrait, nouvelle nomination) : un staff
      // de tournoi sans plus aucun accès ouvert est déconnecté.
      const refreshed = await refreshTournamentAccess(parsed);
      if (!refreshed) {
        await logoutRemote(API_ROOT);
        clearStaffSession();
        return { accessEnded: true };
      }
      return refreshed;
    };
    restore().then((restored) => {
      if (cancelled) return;
      if (restored?.accessEnded) {
        setAuthNotice(TOURNAMENT_ACCESS_ENDED_MESSAGE);
        toast.info(TOURNAMENT_ACCESS_ENDED_MESSAGE, { autoClose: 8000 });
        setUser(null);
      } else {
        setUser(restored);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const passwordHash = await hashPasswordUtil(password);

      // manager-login is anonymous — no Bearer / no shared token. `X-Club` : club demandé (hôte, `?club=`, mémorisé).
      let requestedSlug = getCurrentClubSlug();
      const postLogin = (clubHeaders) =>
        axios.post(
          `${API_BASE_URL}Player/manager-login`,
          {
            Email: email,
            Password: passwordHash,
            device_label: 'erp-web',
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Host-Environment': '.net',
              ...clubHeaders,
            },
          },
        );
      let response;
      try {
        response = await postLogin(clubRequestHeaders());
      } catch (error) {
        // Club mémorisé (ou `?club=`) suspendu depuis : oublié, nouvelle tentative sans club — l'API connecte alors
        // dans un club actif de la personne. Sur l'hôte du club, le club est imposé : refus affiché tel quel.
        if (error.response?.data?.error !== 'club_suspended' || isClubHost() || !requestedSlug) throw error;
        forgetStoredClubSlug();
        requestedSlug = '';
        response = await postLogin({});
      }

      const data = response.data ?? {};
      if (!persistTokenPair(data)) {
        return { success: false, error: 'Réponse login sans access_token.' };
      }

      const userData = mapStaffUser(data.user ?? data, data);

      if (!isStaffRole(userData.role)) {
        clearStaffSession();
        return { success: false, error: 'Accès réservé aux administrateurs.' };
      }
      const tournamentStaff = userData.role === 'tournament_staff';
      const homePath = staffHomePath(userData.role, true, userData);

      // Club du jeton (`club`, absent tant que l'API ne le renvoie pas : club de repli, rien à faire).
      const club = normalizeClubSlug(data.club?.slug) ? data.club : null;
      const clubSlug = normalizeClubSlug(club?.slug);
      const clubName = club?.name || clubSlug;
      if (club && data.requested_club_missing && isClubHost() && tournamentStaff) {
        // Staff de tournoi sur l'hôte du club organisateur : il travaille dans l'ERP de SON club (l'API élève chaque
        // requête vers l'organisateur) ; une session de cet hôte y serait refusée → ERP de son club.
        await logoutRemote(API_ROOT);
        clearStaffSession();
        return { success: true, reloadUrl: clubAppUrl(clubSlug, homePath) };
      }
      if (club && data.requested_club_missing && isClubHost()) {
        // L'hôte fixe le club : une session d'un autre club y serait refusée → connexion sur l'ERP de ce club-là.
        await logoutRemote(API_ROOT);
        clearStaffSession();
        return {
          success: false,
          error: `Ce compte n'est pas staff de ce club. Connecte-toi sur l'ERP de « ${clubName} » : ${clubAppUrl(clubSlug, '/login')}`,
        };
      }
      setSessionClub(club, staffClubsOf(data));
      localStorage.setItem('user', JSON.stringify(userData));
      if (club && clubSlug !== requestedSlug) {
        // Autre club que celui chargé (configuration, sports, terrains) : rechargement complet sur ce club.
        if (data.requested_club_missing) {
          setClubNotice(
            tournamentStaff
              ? `Tes tournois se gèrent depuis l'ERP de ton club : tu es connecté à « ${clubName} ».`
              : `Tu n'es pas staff du club demandé : tu es connecté à « ${clubName} ».`,
          );
        }
        return { success: true, reloadUrl: clubAppUrl(clubSlug, tournamentStaff ? homePath : '/app') };
      }

      setAuthNotice('');
      setUser(userData);
      return { success: true, homePath };
    } catch (error) {
      console.error('Erreur de connexion:', error);
      const data = error.response?.data;
      // Club mémorisé inconnu de l'API : oublié, la prochaine tentative part sans club.
      if (data?.error === 'unknown_club' && !isClubHost()) forgetStoredClubSlug();
      const errorMsg = typeof data === 'string' ? data : data?.message || 'Erreur de connexion';
      return { success: false, error: errorMsg };
    }
  };

  /**
   * Changement de club (super-admin de la Ligue seulement : un compte de club n'a qu'un club) : nouveaux jetons,
   * puis rechargement complet sur ce club. Retourne `{ ok }`, ou `{ ok: false, error }` (`single_club` : refusé
   * par l'API pour un compte de club ; `failed` sinon).
   */
  const switchClub = useCallback(async (club) => {
    const slug = normalizeClubSlug(club?.slug);
    if (!slug) return { ok: false, error: 'failed' };
    // Hôte de club : l'ERP de l'autre club a son propre hôte, donc son propre stockage et sa propre connexion.
    if (!isClubHost()) {
      try {
        if (!(await switchStaffClub(club))) return { ok: false, error: 'failed' };
      } catch (error) {
        if (isSingleClubError(error)) return { ok: false, error: 'single_club' };
        console.error('Changement de club impossible:', error);
        return { ok: false, error: 'failed' };
      }
    }
    window.location.assign(clubAppUrl(slug, '/app'));
    return { ok: true };
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, switchClub, authNotice, clearAuthNotice }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé avec AuthProvider');
  }
  return context;
};
